import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Eye, EyeOff, Trash2, Loader2, ShieldCheck, Star,
  MessageSquare, CheckCircle, Clock, AlertCircle, RefreshCw, ChevronDown, ChevronUp,
  Flag, Ban, XCircle, Users, BookmarkCheck, ShoppingCart, Package, Wand2, Edit3, Save, ToggleLeft, ToggleRight
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { ROUTE_PATHS } from '@/lib/index';
import { staggerContainer, staggerItem } from '@/lib/motion';

// ── Types ─────────────────────────────────────────────────────────────────────
interface AdminReview {
  id: string; meal_name: string; store_name: string | null;
  rating: number; comment: string | null; photo_url: string | null;
  user_name: string | null; user_id: string; is_hidden: boolean; created_at: string;
}

interface Feedback {
  id: string; user_id: string | null; user_email: string | null;
  category: string; message: string; page: string | null;
  store_name: string | null; meal_name: string | null;
  rating: number | null; status: string; admin_note: string | null;
  created_at: string;
}

interface FlaggedRecipe {
  id: string; recipe_name: string; reason: string; details: string | null;
  user_id: string | null; store_name: string | null; status: string; created_at: string;
}

interface BannedRecipe {
  id: string; recipe_name: string; reason: string | null;
  banned_by: string | null; banned_at: string;
  hard_block: boolean; feedback_for_ai: string | null; details: string | null;
}

const CAT_LABELS: Record<string, string> = {
  bug: '🐛 Bug', recipe: '🍽️ Recipe', pricing: '💰 Pricing',
  easy_night: '⚡ Easy Night', feature: '💡 Feature', general: '💬 General',
};

const STATUS_STYLES: Record<string, string> = {
  new:        'bg-blue-500/15 text-blue-600',
  in_review:  'bg-amber-500/15 text-amber-600',
  resolved:   'bg-green-500/15 text-green-600',
  dismissed:  'bg-muted text-muted-foreground',
};

const FLAG_REASONS: Record<string, string> = {
  wrong_protein:    '❌ Wrong protein',
  household_item:   '🏠 Household item',
  bad_recipe:       '🚫 Bad recipe',
  wrong_price:      '💸 Wrong price',
  duplicate:        '🔁 Duplicate',
  inappropriate:    '⚠️ Inappropriate',
  other:            '💬 Other',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const navigate  = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const [tab, setTab] = useState<'overview' | 'feedback' | 'reviews' | 'flagged'>('overview');

  // Overview stats
  const [userStats, setUserStats] = useState({ total_users:0, users_with_saved_recipes:0, total_saved_recipes:0, users_with_grocery_list:0, users_with_pantry:0, total_pantry_items:0, new_feedback:0, pending_flags:0 });

  // Reviews state
  const [reviews,   setReviews]   = useState<AdminReview[]>([]);
  const [revLoad,   setRevLoad]   = useState(true);
  const [revFilter, setRevFilter] = useState<'all'|'visible'|'hidden'>('all');
  const [working,   setWorking]   = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [revStats,  setRevStats]  = useState({ total:0, hidden:0, visible:0 });

  // Feedback state
  const [feedback,  setFeedback]  = useState<Feedback[]>([]);
  const [fbLoad,    setFbLoad]    = useState(true);
  const [fbFilter,  setFbFilter]  = useState<string>('all');
  const [expanded,  setExpanded]  = useState<string | null>(null);
  const [fbStats,   setFbStats]   = useState({ total:0, new_count:0, resolved:0 });
  const [noteEdit,  setNoteEdit]  = useState<{id:string; val:string}|null>(null);

  // Flagged state
  const [flags,       setFlags]      = useState<FlaggedRecipe[]>([]);
  const [banned,      setBanned]     = useState<BannedRecipe[]>([]);
  const [flagLoad,    setFlagLoad]   = useState(true);
  const [flagFilter,  setFlagFilter] = useState<'pending'|'banned'|'dismissed'>('pending');
  const [flagStats,   setFlagStats]  = useState({ pending:0, banned:0, dismissed:0 });
  const [banWorking,  setBanWorking] = useState<string | null>(null);
  const [banModal,    setBanModal]   = useState<FlaggedRecipe | null>(null);
  const [banMode,     setBanMode]    = useState<'fix'|'block'>('fix');
  const [banFeedback, setBanFeedback] = useState('');

  // ── Demo Mode toggle ────────────────────────────────────────────────────────
  const [demoMode, setDemoMode] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const SUPA_URL = 'https://pgjncthsfxsylmvkgibx.supabase.co';
  const SERVICE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc3NzEwMTAxMSwiZXhwIjoyMDkyNjc3MDExfQ.a5Pnv6HhkyxxuGlXSvyjL_E0ZwWtPVHD4PNtgyPJX6A';

  const loadDemoMode = async () => {
    try {
      const res = await fetch(`${SUPA_URL}/rest/v1/app_settings?key=eq.demo_mode&select=value`,
        { headers: { 'apikey': SERVICE_KEY, 'Authorization': `Bearer ${SERVICE_KEY}` } });
      const rows = await res.json() as { value: string }[];
      setDemoMode(rows[0]?.value === 'true');
    } catch { /* silent */ }
  };

  const toggleDemoMode = async () => {
    setDemoLoading(true);
    const next = !demoMode;
    try {
      await fetch(`${SUPA_URL}/rest/v1/app_settings?key=eq.demo_mode`,
        { method: 'PATCH', headers: { 'apikey': SERVICE_KEY, 'Authorization': `Bearer ${SERVICE_KEY}`, 'Content-Type': 'application/json', 'Prefer': 'return=minimal' },
          body: JSON.stringify({ value: next ? 'true' : 'false', updated_at: new Date().toISOString() }) });
      setDemoMode(next);
    } catch { /* silent */ }
    setDemoLoading(false);
  };

  // ── Recipe tweak state ─────────────────────────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [tweakModal,   setTweakModal]   = useState<{ flag: FlaggedRecipe; recipe: Record<string,any> | null } | null>(null);
  const [tweakNote,    setTweakNote]    = useState('');
  const [tweakResult,  setTweakResult]  = useState<Record<string,unknown> | null>(null);
  const [tweakWorking, setTweakWorking] = useState(false);
  const [tweakError,   setTweakError]   = useState('');

  // ── Auth guard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && (!user || !profile?.is_admin)) navigate(ROUTE_PATHS.HOME, { replace: true });
  }, [authLoading, user, profile]);

  useEffect(() => {
    if (profile?.is_admin) { loadOverview(); loadFeedback(); loadReviews(); loadFlagged(); loadDemoMode(); }
  }, [profile]);

  // ── Load overview stats ───────────────────────────────────────────────────
  const loadOverview = async () => {
    const { data } = await supabase.from('admin_user_stats').select('*').single();
    if (data) setUserStats(data as typeof userStats);
  };

  // ── Load reviews ─────────────────────────────────────────────────────────────
  const loadReviews = async () => {
    setRevLoad(true);
    const { data } = await supabase
      .from('dish_reviews')
      .select('id,meal_name,store_name,rating,comment,photo_url,user_name,user_id,is_hidden,created_at')
      .order('created_at', { ascending: false });
    const rows = (data as AdminReview[]) ?? [];
    setReviews(rows);
    setRevStats({ total: rows.length, hidden: rows.filter(r=>r.is_hidden).length, visible: rows.filter(r=>!r.is_hidden).length });
    setRevLoad(false);
  };

  // ── Load feedback ─────────────────────────────────────────────────────────────
  const loadFeedback = async () => {
    setFbLoad(true);
    const { data } = await supabase
      .from('feedback')
      .select('*')
      .order('created_at', { ascending: false });
    const rows = (data as Feedback[]) ?? [];
    setFeedback(rows);
    setFbStats({ total: rows.length, new_count: rows.filter(r=>r.status==='new').length, resolved: rows.filter(r=>r.status==='resolved').length });
    setFbLoad(false);
  };

  // ── Load flagged recipes ──────────────────────────────────────────────────────
  const loadFlagged = async () => {
    setFlagLoad(true);
    const [{ data: flagData }, { data: banData }] = await Promise.all([
      supabase.from('flagged_recipes').select('*').order('created_at', { ascending: false }),
      supabase.from('banned_recipes').select('*').order('banned_at', { ascending: false }),
    ]);
    const flagRows = (flagData as FlaggedRecipe[]) ?? [];
    const banRows  = (banData  as BannedRecipe[])  ?? [];
    setFlags(flagRows);
    setBanned(banRows);
    setFlagStats({
      pending:   flagRows.filter(f => f.status === 'pending').length,
      banned:    flagRows.filter(f => f.status === 'banned').length,
      dismissed: flagRows.filter(f => f.status === 'dismissed').length,
    });
    setFlagLoad(false);
  };

  // ── Review actions ────────────────────────────────────────────────────────────
  const toggleHide = async (r: AdminReview) => {
    setWorking(r.id);
    await supabase.from('dish_reviews').update({ is_hidden: !r.is_hidden }).eq('id', r.id);
    setReviews(prev => prev.map(x => x.id===r.id ? {...x,is_hidden:!x.is_hidden} : x));
    setRevStats(s => ({ ...s, hidden: r.is_hidden?s.hidden-1:s.hidden+1, visible: r.is_hidden?s.visible+1:s.visible-1 }));
    setWorking(null);
  };
  const deleteReview = async (r: AdminReview) => {
    setWorking(r.id);
    if (r.photo_url) { const m=r.photo_url.match(/review-photos\/(.+)$/); if(m) await supabase.storage.from('review-photos').remove([m[1]]); }
    await supabase.from('dish_reviews').delete().eq('id', r.id);
    setReviews(prev => prev.filter(x=>x.id!==r.id));
    setRevStats(s => ({ total:s.total-1, hidden:r.is_hidden?s.hidden-1:s.hidden, visible:r.is_hidden?s.visible:s.visible-1 }));
    setWorking(null); setConfirmId(null);
  };

  // ── Feedback actions ──────────────────────────────────────────────────────────
  const updateFbStatus = async (id: string, status: string) => {
    await supabase.from('feedback').update({ status }).eq('id', id);
    setFeedback(prev => prev.map(f => f.id===id ? {...f,status} : f));
    setFbStats(s => ({ ...s, new_count: feedback.filter(f=>f.status==='new'&&f.id!==id).length + (status==='new'?1:0), resolved: feedback.filter(f=>f.status==='resolved'&&f.id!==id).length + (status==='resolved'?1:0) }));
  };
  const saveNote = async (id: string, note: string) => {
    await supabase.from('feedback').update({ admin_note: note }).eq('id', id);
    setFeedback(prev => prev.map(f => f.id===id ? {...f,admin_note:note} : f));
    setNoteEdit(null);
  };
  const deleteFeedback = async (id: string) => {
    await supabase.from('feedback').delete().eq('id', id);
    setFeedback(prev => prev.filter(f => f.id!==id));
  };

  // ── Flag / ban actions ────────────────────────────────────────────────────────
  const openBanModal = (flag: FlaggedRecipe) => {
    setBanModal(flag);
    setBanMode('fix');
    setBanFeedback(flag.details ?? '');
  };

  const confirmBan = async () => {
    if (!banModal) return;
    setBanWorking(banModal.id);
    await supabase.from('banned_recipes').upsert({
      recipe_name:    banModal.recipe_name,
      reason:         banModal.reason,
      details:        banModal.details,
      banned_by:      user?.email ?? 'admin',
      hard_block:     banMode === 'block',
      feedback_for_ai: banMode === 'fix' ? (banFeedback.trim() || null) : null,
    }, { onConflict: 'recipe_name' });
    await supabase.from('flagged_recipes').update({ status: 'banned' }).eq('recipe_name', banModal.recipe_name);
    setFlags(prev => prev.map(f => f.recipe_name === banModal.recipe_name ? { ...f, status: 'banned' } : f));
    setFlagStats(s => ({ ...s, pending: Math.max(0, s.pending - 1), banned: s.banned + 1 }));
    const { data } = await supabase.from('banned_recipes').select('*').order('banned_at', { ascending: false });
    setBanned((data as BannedRecipe[]) ?? []);
    setBanWorking(null);
    setBanModal(null);
  };

  // ── Recipe tweak functions ─────────────────────────────────────────────────
  const openTweakModal = async (flag: FlaggedRecipe) => {
    setTweakNote(flag.details ?? '');
    setTweakResult(null);
    setTweakError('');
    setTweakModal({ flag, recipe: null });
    // Try to load the actual recipe JSON from the `recipes` table by name
    try {
      const { data } = await supabase
        .from('recipes')
        .select('*')
        .ilike('name', flag.recipe_name)
        .limit(1)
        .single();
      if (data) {
        // Parse JSON fields
        const recipe: Record<string, unknown> = { ...data };
        try { recipe.ingredients = JSON.parse(data.ingredients as string); } catch { /* keep as-is */ }
        try { recipe.instructions = JSON.parse(data.instructions as string); } catch { /* keep as-is */ }
        try { recipe.sides = JSON.parse(data.sides as string); } catch { /* keep as-is */ }
        setTweakModal(prev => prev ? { ...prev, recipe } : null);
      }
    } catch { /* recipe might not be in DB yet, that's ok */ }
  };

  const sendTweakToClaude = async () => {
    if (!tweakModal || !tweakNote.trim()) return;
    setTweakWorking(true);
    setTweakError('');
    setTweakResult(null);
    try {
      const res = await fetch('https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/meal-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMxMzMwMzEsImV4cCI6MjA1ODcwOTAzMX0.kgnb-6jNYUSyJG5tXBzg0f39h4sVFyerS6BRpuQzgHU`,
        },
        body: JSON.stringify({
          action: 'tweak_recipe',
          recipe: tweakModal.recipe ?? { name: tweakModal.flag.recipe_name },
          adminNote: tweakNote,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error ?? 'Tweak failed');
      setTweakResult(data.recipe);
    } catch (e) {
      setTweakError((e as Error).message ?? 'Something went wrong');
    } finally {
      setTweakWorking(false);
    }
  };

  const saveTweakedRecipe = async () => {
    if (!tweakResult || !tweakModal) return;
    // Update the recipe in the DB
    const r = tweakResult as Record<string, unknown>;
    const { error } = await supabase
      .from('recipes')
      .update({
        name:         r.name,
        tagline:      r.tagline,
        ingredients:  typeof r.ingredients === 'string' ? r.ingredients : JSON.stringify(r.ingredients),
        instructions: typeof r.instructions === 'string' ? r.instructions : JSON.stringify(r.instructions),
        sides:        typeof r.sides === 'string' ? r.sides : JSON.stringify(r.sides),
        tip:          r.tip,
        cook_time:    r.cook_time,
        method:       r.method,
        per_serving:  r.per_serving,
        total_cost:   r.total_cost,
      })
      .ilike('name', tweakModal.flag.recipe_name);
    if (error) { setTweakError('Save failed: ' + error.message); return; }
    // Mark the flag as resolved
    await supabase.from('flagged_recipes').update({ status: 'dismissed' }).eq('id', tweakModal.flag.id);
    setFlags(prev => prev.map(f => f.id === tweakModal.flag.id ? { ...f, status: 'dismissed' } : f));
    setTweakModal(null);
  };

  const dismissFlag = async (id: string) => {
    setBanWorking(id);
    await supabase.from('flagged_recipes').update({ status: 'dismissed' }).eq('id', id);
    setFlags(prev => prev.map(f => f.id === id ? { ...f, status: 'dismissed' } : f));
    setFlagStats(s => ({ ...s, pending: Math.max(0, s.pending - 1), dismissed: s.dismissed + 1 }));
    setBanWorking(null);
  };

  const unbanRecipe = async (b: BannedRecipe) => {
    setBanWorking(b.id);
    await supabase.from('banned_recipes').delete().eq('id', b.id);
    setBanned(prev => prev.filter(x => x.id !== b.id));
    setBanWorking(null);
  };

  const filteredReviews  = reviews.filter(r => revFilter==='visible'?!r.is_hidden:revFilter==='hidden'?r.is_hidden:true);
  const filteredFeedback = feedback.filter(f => fbFilter==='all'?true:fbFilter==='new'?f.status==='new':f.category===fbFilter);
  const filteredFlags    = flagFilter === 'banned' ? [] : flags.filter(f => f.status === flagFilter);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!profile?.is_admin) return null;

  return (
    <div className="min-h-screen bg-background pt-12 pb-24">
      {/* Header */}
      <header className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></button>
          <ShieldCheck className="w-5 h-5 text-amber-500" />
          <span className="font-bold text-foreground flex-1">Admin Panel</span>
          <button onClick={() => { loadOverview(); loadFeedback(); loadReviews(); loadFlagged(); }} className="text-muted-foreground hover:text-foreground">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-border/50 overflow-x-auto">
          <button onClick={() => setTab('overview')} className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors border-b-2 whitespace-nowrap ${tab==='overview'?'border-primary text-primary':'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <Users className="w-4 h-4" /> Overview
          </button>
          <button onClick={() => setTab('feedback')} className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors border-b-2 whitespace-nowrap ${tab==='feedback'?'border-primary text-primary':'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <MessageSquare className="w-4 h-4" />
            Feedback
            {fbStats.new_count > 0 && <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">{fbStats.new_count}</span>}
          </button>
          <button onClick={() => setTab('flagged')} className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors border-b-2 whitespace-nowrap ${tab==='flagged'?'border-red-500 text-red-500':'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <Flag className="w-4 h-4" />
            Flagged
            {flagStats.pending > 0 && <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{flagStats.pending}</span>}
          </button>
          <button onClick={() => setTab('reviews')} className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors border-b-2 whitespace-nowrap ${tab==='reviews'?'border-primary text-primary':'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <Star className="w-4 h-4" /> Reviews ({revStats.total})
          </button>
        </div>
      </header>

      <div className="container mx-auto max-w-2xl px-4 py-5 space-y-4">

        {/* ── OVERVIEW TAB ── */}
        {tab === 'overview' && (
          <div className="space-y-4">

            {/* ── Demo Mode Toggle ── */}
            <div className="bg-card border border-border rounded-2xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-foreground text-sm flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${demoMode ? 'bg-amber-400 animate-pulse' : 'bg-green-500'}`} />
                    Demo Mode
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {demoMode
                      ? '⚠️ ON — site is serving pre-built demo recipes. Live data is paused.'
                      : '✅ OFF — site is serving live AI-generated recipes normally.'}
                  </p>
                </div>
                <button onClick={toggleDemoMode} disabled={demoLoading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all disabled:opacity-50 active:scale-95 ${
                    demoMode
                      ? 'bg-amber-500/15 border border-amber-500/30 text-amber-500 hover:bg-amber-500/25'
                      : 'bg-green-500/15 border border-green-500/30 text-green-500 hover:bg-green-500/25'
                  }`}>
                  {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    demoMode ? <><ToggleRight className="w-4 h-4" /> Turn OFF</> : <><ToggleLeft className="w-4 h-4" /> Turn ON</>}
                </button>
              </div>
            </div>

            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">User Data Summary</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Users,         label: 'Total Users',       value: userStats.total_users,               color: 'text-primary' },
                { icon: BookmarkCheck, label: 'Saved Recipes',     value: userStats.total_saved_recipes,        color: 'text-green-500' },
                { icon: Users,         label: 'Users w/ Recipes',  value: userStats.users_with_saved_recipes,   color: 'text-emerald-500' },
                { icon: ShoppingCart,  label: 'Active Lists',      value: userStats.users_with_grocery_list,    color: 'text-blue-500' },
                { icon: Package,       label: 'Pantry Items',      value: userStats.total_pantry_items,         color: 'text-orange-500' },
                { icon: Users,         label: 'Users w/ Pantry',   value: userStats.users_with_pantry,          color: 'text-amber-500' },
                { icon: MessageSquare, label: 'New Feedback',      value: userStats.new_feedback,               color: 'text-violet-500' },
                { icon: Flag,          label: 'Pending Flags',     value: userStats.pending_flags,              color: 'text-red-500' },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl bg-muted flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${s.color}`} />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
                      <p className="text-[10px] text-muted-foreground font-medium leading-tight">{s.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 space-y-2">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">Quick Links</p>
              {[
                { label: '📋 View Feedback', action: () => setTab('feedback') },
                { label: '🚩 Review Flagged Recipes', action: () => setTab('flagged') },
                { label: '⭐ Moderate Reviews', action: () => setTab('reviews') },
              ].map(l => (
                <button key={l.label} onClick={l.action}
                  className="w-full text-left px-3 py-2.5 rounded-xl bg-muted hover:bg-muted/80 text-sm font-semibold text-foreground transition-colors">
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── FEEDBACK TAB ── */}
        {tab === 'feedback' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label:'Total',    value:fbStats.total,     color:'text-foreground' },
                { label:'New',      value:fbStats.new_count, color:'text-blue-500' },
                { label:'Resolved', value:fbStats.resolved,  color:'text-primary' },
              ].map(s => (
                <div key={s.label} className="bg-card border border-border rounded-2xl p-3 text-center">
                  <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Category filter */}
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {[{v:'all',l:'All'},{v:'new',l:'🆕 New'},{v:'bug',l:'🐛 Bug'},{v:'recipe',l:'🍽️ Recipe'},{v:'feature',l:'💡 Feature'},{v:'pricing',l:'💰 Price'},{v:'easy_night',l:'⚡ Easy Night'},{v:'general',l:'💬 General'}].map(f => (
                <button key={f.v} onClick={()=>setFbFilter(f.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${fbFilter===f.v?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                  {f.l}
                </button>
              ))}
            </div>

            {/* Feedback list */}
            {fbLoad ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : filteredFeedback.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm">No feedback in this category</div>
            ) : (
              <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-3">
                {filteredFeedback.map(f => (
                  <motion.div key={f.id} variants={staggerItem}
                    className={`bg-card border rounded-2xl overflow-hidden ${f.status==='new'?'border-blue-500/30':'border-border'}`}>
                    {/* Card header */}
                    <button className="w-full flex items-start gap-3 p-3 text-left" onClick={()=>setExpanded(expanded===f.id?null:f.id)}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{CAT_LABELS[f.category]??f.category}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[f.status]??STATUS_STYLES.new}`}>{f.status.replace('_',' ')}</span>
                          {f.rating && <span className="text-xs text-amber-500 font-bold">{'⭐'.repeat(f.rating)}</span>}
                        </div>
                        <p className="text-sm text-foreground mt-1.5 line-clamp-2 font-medium">{f.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {f.user_email ?? 'Anonymous'} · {f.page ?? 'unknown page'} · {new Date(f.created_at).toLocaleDateString('en-CA',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}
                        </p>
                      </div>
                      {expanded===f.id ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />}
                    </button>

                    {/* Expanded detail */}
                    {expanded === f.id && (
                      <div className="px-3 pb-3 space-y-3 border-t border-border/50 pt-3">
                        <p className="text-sm text-foreground bg-muted rounded-xl px-3 py-2.5">{f.message}</p>

                        {/* Admin note */}
                        {noteEdit?.id === f.id ? (
                          <div className="space-y-2">
                            <textarea value={noteEdit.val} onChange={e=>setNoteEdit({id:f.id,val:e.target.value})}
                              placeholder="Add a note..." rows={2}
                              className="w-full px-3 py-2 bg-muted border border-border rounded-xl text-xs resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
                            <div className="flex gap-2">
                              <button onClick={()=>saveNote(f.id,noteEdit.val)} className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold">Save</button>
                              <button onClick={()=>setNoteEdit(null)} className="px-3 py-1.5 bg-muted text-muted-foreground rounded-lg text-xs">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={()=>setNoteEdit({id:f.id,val:f.admin_note??''})}
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                            {f.admin_note ? `📝 ${f.admin_note}` : '+ Add note'}
                          </button>
                        )}

                        {/* Status + delete actions */}
                        <div className="flex gap-2 flex-wrap">
                          {(['new','in_review','resolved','dismissed'] as const).map(s => (
                            <button key={s} onClick={()=>updateFbStatus(f.id,s)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${f.status===s?STATUS_STYLES[s]+' font-bold':'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                              {s==='new' && <Clock className="w-3 h-3 inline mr-1" />}
                              {s==='resolved' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                              {s==='in_review' && <AlertCircle className="w-3 h-3 inline mr-1" />}
                              {s.replace('_',' ')}
                            </button>
                          ))}
                          <button onClick={()=>deleteFeedback(f.id)} className="ml-auto p-1.5 rounded-lg text-red-400 hover:bg-red-400/10 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            )}
          </>
        )}

        {/* ── FLAGGED TAB ── */}
        {tab === 'flagged' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label:'Pending',   value:flagStats.pending,   color:'text-red-500' },
                { label:'Banned',    value:flagStats.banned,    color:'text-amber-500' },
                { label:'Dismissed', value:flagStats.dismissed, color:'text-muted-foreground' },
              ].map(s => (
                <div key={s.label} className="bg-card border border-border rounded-2xl p-3 text-center">
                  <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2">
              {([
                { v: 'pending',   l: '⏳ Pending' },
                { v: 'banned',    l: '🔴 Banned List' },
                { v: 'dismissed', l: '✅ Dismissed' },
              ] as const).map(f => (
                <button key={f.v} onClick={() => setFlagFilter(f.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${flagFilter===f.v?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                  {f.l}
                </button>
              ))}
            </div>

            {flagLoad ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : flagFilter === 'banned' ? (
              /* ── Banned list ── */
              banned.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">
                  <Ban className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  No banned recipes yet
                </div>
              ) : (
                <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-3">
                  {banned.map(b => (
                    <motion.div key={b.id} variants={staggerItem}
                      className="bg-card border border-red-500/30 rounded-2xl p-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
                        <Ban className="w-4 h-4 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="text-sm font-bold text-foreground truncate">{b.recipe_name}</p>
                          {b.hard_block
                            ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 whitespace-nowrap">🚫 Hard Block</span>
                            : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 whitespace-nowrap">📝 Fix & Learn</span>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {b.reason ?? 'no reason'} · {new Date(b.banned_at).toLocaleDateString('en-CA',{month:'short',day:'numeric'})}
                        </p>
                        {!b.hard_block && b.feedback_for_ai && (
                          <p className="text-xs text-green-400/80 mt-0.5 italic line-clamp-2">AI note: "{b.feedback_for_ai}"</p>
                        )}
                      </div>
                      <button
                        onClick={() => unbanRecipe(b)}
                        disabled={banWorking === b.id}
                        title="Remove ban"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors shrink-0">
                        {banWorking === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                      </button>
                    </motion.div>
                  ))}
                </motion.div>
              )
            ) : filteredFlags.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm">
                <Flag className="w-10 h-10 mx-auto mb-3 opacity-30" />
                No {flagFilter} reports
              </div>
            ) : (
              /* ── Pending / Dismissed flags ── */
              <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-3">
                {filteredFlags.map(flag => (
                  <motion.div key={flag.id} variants={staggerItem}
                    className={`bg-card border rounded-2xl overflow-hidden ${flag.status==='pending'?'border-red-500/30':'border-border'}`}>
                    <div className="p-3">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${flag.status==='pending'?'bg-red-500/10':'bg-muted'}`}>
                          <Flag className={`w-4 h-4 ${flag.status==='pending'?'text-red-500':'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${flag.status==='pending'?'bg-red-500/15 text-red-500':'bg-muted text-muted-foreground'}`}>
                              {flag.status}
                            </span>
                            <span className="text-xs font-semibold bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                              {FLAG_REASONS[flag.reason] ?? flag.reason}
                            </span>
                          </div>
                          <p className="text-sm font-bold text-foreground leading-snug">{flag.recipe_name}</p>
                          {flag.details && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 italic">"{flag.details}"</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {flag.store_name ?? 'Unknown store'} · {new Date(flag.created_at).toLocaleDateString('en-CA',{month:'short',day:'numeric'})}
                          </p>
                        </div>
                      </div>
                    </div>
                    {flag.status === 'pending' && (
                      <div className="flex border-t border-border/50">
                        <button
                          onClick={() => openTweakModal(flag)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-violet-400 hover:bg-violet-500/10 transition-colors">
                          <Wand2 className="w-3.5 h-3.5" /> Tweak with Claude
                        </button>
                        <div className="w-px bg-border/50" />
                        <button
                          onClick={() => openBanModal(flag)}
                          disabled={banWorking === flag.id}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-red-500 hover:bg-red-500/10 transition-colors">
                          {banWorking === flag.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <><Ban className="w-3.5 h-3.5" /> Ban Recipe</>}
                        </button>
                        <div className="w-px bg-border/50" />
                        <button
                          onClick={() => dismissFlag(flag.id)}
                          disabled={banWorking === flag.id}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-muted-foreground hover:bg-muted/50 transition-colors">
                          <XCircle className="w-3.5 h-3.5" /> Dismiss
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            )}
          </>
        )}

        {/* ── REVIEWS TAB ── */}
        {tab === 'reviews' && (
          <>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label:'Total', value:revStats.total, color:'text-foreground' },
                { label:'Visible', value:revStats.visible, color:'text-primary' },
                { label:'Hidden', value:revStats.hidden, color:'text-amber-500' },
              ].map(s => (
                <div key={s.label} className="bg-card border border-border rounded-2xl p-3 text-center">
                  <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              {(['all','visible','hidden'] as const).map(f => (
                <button key={f} onClick={()=>setRevFilter(f)}
                  className={`px-4 py-2 rounded-full text-xs font-bold capitalize transition-colors ${revFilter===f?'bg-primary text-primary-foreground':'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                  {f} ({f==='all'?revStats.total:f==='visible'?revStats.visible:revStats.hidden})
                </button>
              ))}
            </div>

            {revLoad ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : filteredReviews.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm">No reviews in this category</div>
            ) : (
              <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-3">
                {filteredReviews.map(r => (
                  <motion.div key={r.id} variants={staggerItem}
                    className={`bg-card border rounded-2xl overflow-hidden transition-opacity ${r.is_hidden?'opacity-50 border-amber-500/30':'border-border'}`}>
                    <div className="flex gap-3 p-3">
                      {r.photo_url && <img src={r.photo_url} alt={r.meal_name} className="w-20 h-20 object-cover rounded-xl shrink-0" loading="lazy" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-bold text-sm text-foreground truncate">{r.meal_name}</p>
                            <p className="text-xs text-muted-foreground">{r.user_name??'Anon'} · {r.store_name??'No store'} · {new Date(r.created_at).toLocaleDateString('en-CA',{month:'short',day:'numeric'})}</p>
                            <div className="flex gap-0.5 mt-1">{[1,2,3,4,5].map(n=><Star key={n} className={`w-3 h-3 ${n<=r.rating?'fill-amber-400 text-amber-400':'text-muted-foreground/30'}`} />)}</div>
                          </div>
                          {r.is_hidden && <span className="text-[10px] font-bold bg-amber-500/15 text-amber-600 px-2 py-0.5 rounded-full shrink-0">HIDDEN</span>}
                        </div>
                        {r.comment && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2 italic">"{r.comment}"</p>}
                      </div>
                    </div>
                    <div className="flex border-t border-border/50">
                      <button onClick={()=>toggleHide(r)} disabled={working===r.id}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors ${r.is_hidden?'text-primary hover:bg-primary/10':'text-amber-600 hover:bg-amber-500/10'}`}>
                        {working===r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : r.is_hidden ? <><Eye className="w-3.5 h-3.5"/>Unhide</> : <><EyeOff className="w-3.5 h-3.5"/>Hide</>}
                      </button>
                      <div className="w-px bg-border/50" />
                      {confirmId===r.id ? (
                        <>
                          <button onClick={()=>deleteReview(r)} disabled={working===r.id} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-red-500 hover:bg-red-500/10 transition-colors">
                            {working===r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Trash2 className="w-3.5 h-3.5"/>Confirm</>}
                          </button>
                          <div className="w-px bg-border/50" />
                          <button onClick={()=>setConfirmId(null)} className="flex-1 flex items-center justify-center py-2.5 text-xs text-muted-foreground hover:bg-muted/50 transition-colors">Cancel</button>
                        </>
                      ) : (
                        <button onClick={()=>setConfirmId(r.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-red-400 hover:bg-red-400/10 transition-colors">
                          <Trash2 className="w-3.5 h-3.5"/>Delete
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* ── Tweak Modal ── */}
      {tweakModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-card border border-border rounded-t-3xl shadow-2xl overflow-hidden max-h-[90dvh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-border/60 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
                  <Wand2 className="w-4 h-4 text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-black text-sm text-foreground">Tweak with Claude</h3>
                  <p className="text-xs text-muted-foreground truncate">"{tweakModal.flag.recipe_name}"</p>
                </div>
                <button onClick={() => setTweakModal(null)} className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-muted/80">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">

              {/* User's original flag info */}
              <div className="rounded-xl bg-red-500/8 border border-red-500/20 px-3 py-2.5">
                <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-1">
                  {FLAG_REASONS[tweakModal.flag.reason] ?? tweakModal.flag.reason} — User Flag
                </p>
                <p className="text-xs text-muted-foreground italic">
                  {tweakModal.flag.details ? `"${tweakModal.flag.details}"` : 'No details provided'}
                </p>
              </div>

              {/* Current recipe preview */}
              {tweakModal.recipe && (
                <div className="rounded-xl bg-muted/30 border border-border px-3 py-2.5">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Edit3 className="w-3 h-3" /> Current Recipe
                  </p>
                  {/* Ingredients list */}
                  {Array.isArray((tweakModal.recipe as Record<string,unknown>).ingredients) && (
                    <div className="space-y-1">
                      {((tweakModal.recipe as Record<string,unknown>).ingredients as {name:string;qty?:string;price?:number;on_sale?:boolean}[]).map((ing, i) => (
                        <div key={i} className={`flex items-center gap-2 text-xs ${ing.on_sale ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                          {ing.on_sale && <span className="text-[9px] bg-primary text-white px-1 rounded font-bold">SALE</span>}
                          <span className="flex-1">{ing.name}</span>
                          {ing.qty && <span className="text-muted-foreground/70">{ing.qty}</span>}
                          {ing.price ? <span>${Number(ing.price).toFixed(2)}</span> : null}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Instructions preview */}
                  {Array.isArray((tweakModal.recipe as Record<string,unknown>).instructions) && (
                    <div className="mt-2 space-y-1">
                      {((tweakModal.recipe as Record<string,unknown>).instructions as string[]).slice(0,3).map((step, i) => (
                        <p key={i} className="text-[10px] text-muted-foreground/70 line-clamp-1">Step {i+1}: {step}</p>
                      ))}
                      {((tweakModal.recipe as Record<string,unknown>).instructions as string[]).length > 3 && (
                        <p className="text-[10px] text-muted-foreground/50">+ {((tweakModal.recipe as Record<string,unknown>).instructions as string[]).length - 3} more steps…</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {!tweakModal.recipe && (
                <div className="rounded-xl bg-muted/20 border border-border px-3 py-2.5 text-xs text-muted-foreground text-center">
                  Recipe not found in DB yet — Claude will build from scratch using your instructions
                </div>
              )}

              {/* Admin instruction field */}
              <div>
                <label className="block text-xs font-bold text-foreground mb-1.5 flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5 text-violet-400" />
                  Tell Claude what to fix
                </label>
                <textarea
                  value={tweakNote}
                  onChange={e => setTweakNote(e.target.value)}
                  placeholder="e.g. Replace the lamb with ground beef — lamb is too expensive. Keep all other ingredients. Also reduce cook time to 30 min."
                  rows={4}
                  className="w-full text-sm bg-muted/40 border border-border rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/40 placeholder:text-muted-foreground/50"
                />
              </div>

              {/* Error */}
              {tweakError && (
                <div className="rounded-xl bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
                  {tweakError}
                </div>
              )}

              {/* Claude's result */}
              {tweakResult && (
                <div className="rounded-xl bg-green-500/8 border border-green-500/25 px-3 py-3 space-y-2">
                  <p className="text-[10px] font-bold text-green-400 uppercase tracking-wider flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /> Claude's Tweaked Version
                  </p>
                  <p className="text-sm font-bold text-foreground">{String((tweakResult as Record<string,unknown>).name ?? '')}</p>
                  {(tweakResult as Record<string,unknown>).tagline && (
                    <p className="text-xs text-muted-foreground italic">{String((tweakResult as Record<string,unknown>).tagline)}</p>
                  )}
                  {Array.isArray((tweakResult as Record<string,unknown>).ingredients) && (
                    <div className="space-y-1">
                      {((tweakResult as Record<string,unknown>).ingredients as {name:string;qty?:string;price?:number;on_sale?:boolean}[]).map((ing, i) => (
                        <div key={i} className={`flex items-center gap-2 text-xs ${ing.on_sale ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                          {ing.on_sale && <span className="text-[9px] bg-primary text-white px-1 rounded font-bold">SALE</span>}
                          <span className="flex-1">{ing.name}</span>
                          {ing.qty && <span className="text-muted-foreground/70">{ing.qty}</span>}
                          {ing.price ? <span>${Number(ing.price).toFixed(2)}</span> : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="shrink-0 border-t border-border/60">
              {!tweakResult ? (
                <button
                  onClick={sendTweakToClaude}
                  disabled={tweakWorking || !tweakNote.trim()}
                  className="w-full py-3.5 flex items-center justify-center gap-2 text-sm font-bold text-white bg-violet-600 hover:bg-violet-700 active:scale-98 transition-all disabled:opacity-50">
                  {tweakWorking
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending to Claude…</>
                    : <><Wand2 className="w-4 h-4" /> Send to Claude</>}
                </button>
              ) : (
                <div className="flex">
                  <button
                    onClick={() => { setTweakResult(null); setTweakError(''); }}
                    className="flex-1 py-3 text-sm font-semibold text-muted-foreground hover:bg-muted/40 transition-colors">
                    ← Try Again
                  </button>
                  <div className="w-px bg-border/60" />
                  <button
                    onClick={saveTweakedRecipe}
                    className="flex-1 py-3 flex items-center justify-center gap-1.5 text-sm font-bold text-green-500 hover:bg-green-500/10 transition-colors">
                    <Save className="w-4 h-4" /> Save & Resolve
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Ban Modal ── */}
      {banModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-card border border-border rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-4 border-b border-border/60">
              <h3 className="font-black text-base text-foreground">Ban Recipe</h3>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">"{banModal.recipe_name}"</p>
            </div>

            <div className="p-4 space-y-4">
              {/* Mode selector */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setBanMode('fix')}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all text-center ${
                    banMode === 'fix'
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-border bg-muted/30 hover:bg-muted/60'
                  }`}>
                  <span className="text-xl">📝</span>
                  <span className="text-xs font-bold text-foreground">Fix &amp; Learn</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">AI gets notes, can try again</span>
                </button>
                <button
                  onClick={() => setBanMode('block')}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all text-center ${
                    banMode === 'block'
                      ? 'border-red-500 bg-red-500/10'
                      : 'border-border bg-muted/30 hover:bg-muted/60'
                  }`}>
                  <span className="text-xl">🚫</span>
                  <span className="text-xs font-bold text-foreground">Hard Block Forever</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">Never generate again</span>
                </button>
              </div>

              {/* Feedback textarea — only for Fix & Learn */}
              {banMode === 'fix' && (
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                    What should AI fix? <span className="text-muted-foreground/60 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={banFeedback}
                    onChange={e => setBanFeedback(e.target.value)}
                    placeholder="e.g. Use real chicken breast, not deli meat. Keep it under 5 ingredients."
                    rows={3}
                    className="w-full text-sm bg-muted/40 border border-border rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground/50"
                  />
                </div>
              )}
            </div>

            <div className="flex border-t border-border/60">
              <button
                onClick={() => setBanModal(null)}
                className="flex-1 py-3 text-sm font-semibold text-muted-foreground hover:bg-muted/40 transition-colors">
                Cancel
              </button>
              <div className="w-px bg-border/60" />
              <button
                onClick={confirmBan}
                disabled={!!banWorking}
                className={`flex-1 py-3 text-sm font-bold transition-colors flex items-center justify-center gap-1.5 ${
                  banMode === 'block'
                    ? 'text-red-500 hover:bg-red-500/10'
                    : 'text-green-500 hover:bg-green-500/10'
                }`}>
                {banWorking
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : banMode === 'block' ? '🚫 Hard Block' : '📝 Confirm Fix & Learn'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
